import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../config/app-config';
// File: src/components/search/SearchResult.jsx
import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    IconButton,
    Collapse,
    CircularProgress
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { transformArticleHTML } from '../../utils/html/transformHtml';


const SearchResult = ({
    result,
    searchQuery,
    onOpenResult,
    getHighlightedPreview,
    selectedRelease,
    tocData,
    offlineMode,
    fetchArticle,
}) => {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewContent, setPreviewContent] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const previewCtrlRef = React.useRef(null);
    React.useEffect(() => () => previewCtrlRef.current?.abort?.(), []);

    const togglePreview = async () => {
        if (!previewOpen && previewContent === null) {
            setLoadingPreview(true);

            // Alte Preview abbrechen & neuen Controller setzen
            previewCtrlRef.current?.abort?.();
            const controller = new AbortController();
            previewCtrlRef.current = controller;

            await fetchArticle(result._id, (data) => {
                const transformed = transformArticleHTML(
                    data.body,
                    selectedRelease,
                    tocData,
                    offlineMode
                );
                setPreviewContent(transformed);
                setLoadingPreview(false);
                setPreviewOpen(true);
            });
        } else {
            setPreviewOpen((o) => !o);
        }
    };

    const openArticleInternal = (e) => {
        e.stopPropagation();
        onOpenResult(result);
    };

    const openArticleNewTab = (e) => {
        e.stopPropagation();
        // URL nach dem internen Routing-Muster
        const params = selectedRelease.toSearchParams({ articleId: result._id });
        const url = `${window.location.origin}/?${params}`;
        window.open(url, '_blank');
    };

    return (
        <Paper
            elevation={0}
            onClick={togglePreview}
            sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: 'background.default',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 }
            }}
        >
            {/* Kopfzeile */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex="1">
                    {/* Titel klickbar (intern) */}
                    <Typography
                        variant="h6"
                        gutterBottom
                        onClick={openArticleInternal}
                        sx={{
                            fontFamily: FONTS.heading,
                            fontWeight: 500,
                            color: 'accent.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {result.title}
                    </Typography>

                    {/* Keywords als Breadcrumb */}
                    {result.metadata?.keywords && (
                        <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                            sx={{
                                color: darkMode ? '#bbb' : 'text.secondary',
                                mb: 1
                            }}
                        >
                            {result.metadata.keywords.join(' › ')}
                        </Typography>
                    )}

                    {/* Kurz-Vorschau (2 Zeilen) */}
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: FONTS.body,
                            color: darkMode ? '#ddd' : 'text.secondary',
                            lineHeight: 1.4,
                            maxHeight: '2.8em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                        dangerouslySetInnerHTML={{
                            __html: getHighlightedPreview(result.body, searchQuery)
                        }}
                    />
                </Box>

                {/* External link -> new tab */}
                <IconButton
                    onClick={openArticleNewTab}
                    size="small"
                    sx={{ color: darkMode ? theme.palette.text.primary : 'inherit', ml: 1 }}
                >
                    <OpenInNewIcon />
                </IconButton>
            </Box>

            {/* Expandable preview */}
            <Collapse in={previewOpen} timeout="auto" unmountOnExit>
                <Box
                    mt={2}
                    p={2}

                    // Apply dark mode to the HTML preview
                    className={darkMode ? 'dark-mode' : ''}
                    sx={{
                        borderLeft: 4,
                        borderColor: darkMode ? '#555' : 'divider',
                        bgcolor: darkMode ? theme.palette.background.default : '#f5f5f5',

                        // links use brand accent in dark mode
                        ...(darkMode
                            ? { '& a': { color: 'accent.main' } }
                            : {}
                        ),
                    }}
                >
                    {loadingPreview ? (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress size={20} />
                        </Box>
                    ) : previewContent !== null ? (
                        typeof previewContent === 'string' ? (
                            <div
                                dangerouslySetInnerHTML={{ __html: previewContent }}
                                style={{ color: darkMode ? theme.palette.text.primary : 'inherit', fontFamily: FONTS.body }}
                            />
                        ) : (
                            previewContent
                        )
                    ) : (
                        <Typography
                            variant="body2"
                            color={darkMode ? '#bbb' : 'text.secondary'}
                        >
                            No preview available.
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default SearchResult;

